<template>
  <section
    v-if="serviceCenterLandingPage && serviceCenterLandingPage.publication"
    class="faqs text-base text-tdefault-color"
  >
    <div class="grid grid-cols-2 p-6 bg-primary-default">
      <div class="">
        <label class="text-white font-bold text-base">Wat zoekt u?</label>
        <v-input-group-simple-2
          class="mt-2"
          id="searchFaqs"
          v-model="searchText"
          type="text"
          @input="searchInput($event.target.value)"
          @keyup.enter="searchFaqs"
        />
      </div>
      <div class="grid-align-start content-end ml-4">
        <button
          @click.prevent="searchFaqs"
          class="flex justify-center py-2 px-2 md:px-4 shadow-sm text-sm font-medium text-white bg-cta rounded-full hover:bg-primary-default/50 items-center mr-2 mb-1.5"
        >
          <MagnifyingGlassIcon class="w-5 h-5 text-white" />
        </button>
      </div>
    </div>
    <div v-if="detailsBlock" class="grid grid-rows px-6 py-2">
      <div class="grid-align-start">
        <button
          @click.prevent="backToLandingPage()"
          class="flex justify-center text-xs md:text-sm lg:text-base font-medium text-pdsPrimaryColor hover:bg-primary-default/50"
        >
          <ArrowLeftIcon class="h-5 w-5 flex-shrink-0 mr-2 text-primary-default" aria-hidden="true" />
          <span class="">Terug</span>
        </button>
      </div>
      <div v-if="selectedFaq.name" class="">
        <h2 class="font-bold mb-2">{{ selectedFaq.name }}</h2>
        <span
          class="content"
          v-html="
            selectedFaq.schema_elements && selectedFaq.schema_elements[0].content
              ? selectedFaq.schema_elements[0].content
              : selectedFaq.contentsnippet
              ? selectedFaq.contentsnippet
              : ''
          "
        />
      </div>
    </div>
    <div v-if="showFaqs" class="grid grid-cols-2 gap-x-2 px-6 py-2">
      <h2 v-if="detailsBlock && selectedFaq.name" class="text-pdsPrimaryColor text-xl mb-2 col-span-2">
        Gerelateerde vragen
      </h2>
      <h2
        v-if="detailsBlock && collectionDetails && !selectedFaq.name"
        class="text-pdsPrimaryColor text-base mb-2 col-span-2"
      >
        Categorie: {{ selectedCollection.name }}
      </h2>
      <h2
        v-if="detailsBlock && showSearchResults && !selectedFaq.name"
        class="text-pdsPrimaryColor text-base mb-2 col-span-2"
      >
        Zoekresultaten voor ‘{{ searchText }}’
      </h2>
      <button
        @click.prevent="goToDetails(question)"
        v-for="question in computedFaqs"
        :key="question.id"
        class="flex justify-left text-left py-2 text-base font-medium text-tdefault-color hover:bg-primary-default/50 mb-2"
      >
        <BookmarkIcon class="h-5 w-5 flex-shrink-0 mr-2 text-primary-default" aria-hidden="true" />
        <span class="">{{ question.name }}</span>
      </button>
    </div>
    <div v-if="showCollections" class="flex flex-row flex-wrap px-6 pt-2 pb-6">
      <h2 class="text-pdsPrimaryColor font-bold text-xl w-full">Onderwerpen</h2>
      <div
        v-for="(collection, index) in serviceCenterLandingPage.publication.collections"
        :key="index"
        class="grid-align-start mr-4 mb-2"
      >
        <button
          @click.prevent="goToCollectionDetails(collection, $event)"
          class="flex justify-left text-left py-2 text-base font-medium text-tdefault-color hover:bg-primary-default/50"
        >
          <span class="">{{ collection.name }}</span>
          <ArrowRightIcon class="h-4 w-4 flex-shrink-0 ml-1 text-primary-default self-center" aria-hidden="true" />
        </button>
      </div>
    </div>
  </section>
</template>

<script>
import { useStore } from 'vuex';
import { computed } from 'vue';
import { MagnifyingGlassIcon, ArrowLeftIcon, ArrowRightIcon } from '@heroicons/vue/24/outline';
import { BookmarkIcon } from '@heroicons/vue/20/solid';

export default {
  name: 'Faqs',
  components: { MagnifyingGlassIcon, ArrowLeftIcon, ArrowRightIcon, BookmarkIcon },
  setup() {
    const store = useStore();

    const serviceCenterLandingPage = computed(() => store.getters['patient/getServiceCenterDetails']);
    const serviceCenterSearchDetails = computed(() => store.getters['patient/getServiceCenterSearchDetails']);
    const serviceCenterRelatedQuestions = computed(() => store.getters['patient/getServiceCenterRelatedQuestions']);

    const fetchServiceCenterDetails = () => store.dispatch('patient/fetchServiceCenterDetails');
    const fetchServiceCenterRelatedFaqs = (value) => store.dispatch('patient/fetchServiceCenterRelatedFaqs', value);
    const fetchServiceCenterSearchFaqs = (value) => store.dispatch('patient/fetchServiceCenterSearchFaqs', value);

    return {
      serviceCenterLandingPage,
      serviceCenterSearchDetails,
      serviceCenterRelatedQuestions,
      fetchServiceCenterDetails,
      fetchServiceCenterRelatedFaqs,
      fetchServiceCenterSearchFaqs
    };
  },
  data() {
    return {
      searchText: '',
      showFaqs: true,
      showCollections: true,
      detailsBlock: false,
      collectionDetails: false,
      computedFaqs: [],
      selectedCollection: {},
      selectedFaq: {},
      showSearchResults: false
    };
  },
  created() {
    this.fetchServiceCenterDetails().then(() => {
      if (this.serviceCenterLandingPage && this.serviceCenterLandingPage.publication) {
        this.loadComputedFaqs();
      }
    });
  },
  methods: {
    loadComputedFaqs() {
      this.computedFaqs = Object.assign([], this.serviceCenterLandingPage.publication.mostpopular);
      if (this.detailsBlock) {
        this.computedFaqs = Object.assign([], this.serviceCenterRelatedQuestions);
        if (this.selectedFaq.name && this.showSearchResults) {
          this.computedFaqs = Object.assign([], this.serviceCenterSearchDetails);
        }
        if (this.selectedFaq.name && this.selectedCollection.name && this.collectionDetails) {
          this.computedFaqs = Object.assign([], this.selectedCollection.mostpopularItems);
        }
      }
    },
    goToDetails(question) {
      this.fetchServiceCenterRelatedFaqs({ articleId: question.id, collectionId: question.collections[0].id }).then(
        (response) => {
          this.detailsBlock = true;
          this.loadComputedFaqs();
          this.selectedFaq = question;
          if (response && response.article.schema_elements && response.article.schema_elements[0].content) {
            this.selectedFaq.schema_elements = response.article.schema_elements;
          }
          this.showCollections = false;
        }
      );
    },
    backToLandingPage() {
      this.detailsBlock = true;
      this.showCollections = false;
      if (this.selectedFaq.name && this.showSearchResults) {
        this.loadComputedFaqs();
        this.collectionDetails = false;
        this.selectedFaq = {};
      } else if (this.selectedFaq.name && this.selectedCollection.name && this.collectionDetails) {
        this.loadComputedFaqs();
        this.collectionDetails = true;
        this.selectedFaq = {};
      } else {
        this.detailsBlock = false;
        this.showCollections = true;
        this.showSearchResults = false;
        this.loadComputedFaqs();
        this.collectionDetails = false;
        this.selectedCollection = {};
        this.selectedFaq = {};
      }
    },
    goToCollectionDetails(collection, event) {
      event.preventDefault();
      this.selectedCollection = collection;
      this.computedFaqs = Object.assign([], collection.mostpopularItems);
      this.detailsBlock = true;
      this.collectionDetails = true;
      this.showCollections = false;
    },
    searchFaqs() {
      if (!this.searchText) {
        return;
      }
      this.fetchServiceCenterSearchFaqs(this.searchText).then(() => {
        if (this.serviceCenterSearchDetails) {
          this.computedFaqs = Object.assign([], this.serviceCenterSearchDetails);
        }
      });
      this.selectedFaq = {};
      this.collectionDetails = false;
      this.detailsBlock = true;
      this.showSearchResults = true;
      this.showCollections = false;
    },
    searchInput(value) {
      this.searchText = value;
    }
  }
};
</script>
